import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import { Reveal, SafeImg } from "@/components/site/Reveal";
import { useLang } from "@/lib/i18n";
import { L } from "@/lib/localize";
import { useAssets, leaderPortrait } from "@/lib/assets";
import api from "@/lib/api";
import JsonLd from "@/components/site/JsonLd";
import { graph, breadcrumbNode, itemListNode } from "@/lib/schema";
import { useSeo } from "@/lib/seo";

export default function Leaders() {
  const { t, lang } = useLang();
  const assets = useAssets();
  useSeo({ title: t("seo.leaders.title"), description: t("seo.leaders.desc"), path: "/leader" });
  const [leaders, setLeaders] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const jsonLd = useMemo(
    () =>
      graph([
        breadcrumbNode([{ name: "Home", path: "/" }, { name: t("nav.leaders"), path: "/leader" }], lang),
        itemListNode({ lang, items: leaders.map((x) => ({ name: L(x, lang).name, path: `/leader/${x.slug}` })) }),
      ]),
    [leaders, lang, t]
  );

  useEffect(() => {
    api.get("/leaders").then((r) => setLeaders(r.data)).catch(() => {}).finally(() => setLoaded(true));
  }, []);

  return (
    <div className="bg-obsidian text-cream min-h-screen">
      <Nav />
      <JsonLd id="jsonld-leaders" data={jsonLd} />
      <section className="pt-32 sm:pt-44 pb-16">
        <div className="container-x">
          <Reveal>
            <p className="eyebrow mb-3">{t("home.leaders.eyebrow")}</p>
            <h1 className="font-serif font-semibold text-5xl sm:text-7xl leading-[0.95]" data-testid="leaders-title">{t("nav.leaders")}</h1>
            <p className="mt-5 max-w-xl text-cream2">Ogni viaggio nasce dalla conoscenza profonda del territorio e delle culture che lo popolano. Ogni guida ha un legame unico con i luoghi ed i misteri che ti porterà a scoprire.</p>
          </Reveal>
        </div>
      </section>

      <section className="pb-24 sm:pb-32" data-testid="leaders-grid">
        <div className="container-x grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 sm:gap-8">
          {leaders.map((l0, i) => {
            const l = L(l0, lang);
            const portrait = leaderPortrait(assets, l);
            return (
              <Reveal key={l0.id} delay={(i % 4) * 0.08} className="h-full">
                <Link to={`/leader/${l0.slug}`} className="group block h-full" data-testid={`leader-card-${l0.slug}`}>
                  <div className="relative overflow-hidden border border-white/5 group-hover:border-copper/50 transition-colors duration-500">
                    <div className="aspect-[4/5] sm:aspect-[3/4] overflow-hidden">
                      <SafeImg src={portrait} alt={l.name} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 group-hover:scale-105" />
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-obsidian/70 via-transparent to-transparent pointer-events-none" />
                    <div className="absolute bottom-3 left-3 right-3 font-mono text-[10px] uppercase tracking-[0.18em] text-cream2">{l.territory}</div>
                  </div>
                  <h2 className="font-serif text-xl sm:text-2xl text-cream mt-3 sm:mt-4 group-hover:text-copperlight transition-colors">{l.name}</h2>
                  <p className="font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.15em] text-sand mt-1 leading-relaxed">{l.role}</p>
                  {l.philosophy ? <p className="mt-3 text-cream2/90 font-serif text-base sm:text-lg italic leading-snug">“{l.philosophy}”</p> : null}
                </Link>
              </Reveal>
            );
          })}
          {loaded && leaders.length === 0 && <p className="text-sand col-span-full text-center py-20">Nessun tour leader pubblicato per ora.</p>}
        </div>
      </section>
      <Footer />
    </div>
  );
}
